import { Ionicons } from "@expo/vector-icons";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { createNativeStackNavigator } from "@react-navigation/native-stack";

import * as React from "react";

/**
 * Web-Exclusive Pages: Navigation Pattern
 *
 * This app uses platform-specific file extensions for web-exclusive pages:
 * - .web.jsx files are resolved by Metro when Platform.OS === 'web'
 * - .native.js files are resolved on iOS/Android
 * - .js files are the fallback for all platforms
 *
 * Resolution order on web: Screen.web.jsx -> Screen.js
 * Resolution order on native: Screen.native.js -> Screen.js
 *
 * Web screens are located in the same directories as mobile screens:
 * - src/screens/Home/Home.web.jsx (Doctor web dashboard)
 * - src/screens/Home/HomeFacility.web.jsx (Facility web dashboard)
 * - src/screens/Auth/Login/Login.web.jsx (Web login)
 * - etc.
 *
 * The index.js in each screen folder provides explicit Platform.select()
 * exports for tree-shaking optimization.
 */
import { Platform, View, Text } from "react-native";
import { useSelector } from "react-redux";
import { CheckinButton } from "../components/all";
import aboveX from "../constants/aboveX";
import { useSafeAreaInsets } from "react-native-safe-area-context";

// Auth
import LoginScreen from "../screens/Auth/Login";
import ClinicLocatorScreen from "../screens/Auth/Register/ClinicLocator";
import PaymentSetupScreen from "../screens/Auth/Register/PaymentSetup";
import RegisterScreen from "../screens/Auth/Register/Register";
import CompleteRegistrationScreen from "../screens/Auth/Register/StepComplete";
import CategoryScreen from "../screens/Auth/Register/StepCategory";
import ExpertiseScreen from "../screens/Auth/Register/StepExpertise";
// import StepProfession from "../screens/Auth/Register/StepProfession";
import StepCountry from "../screens/Auth/Register/StepCountry";

import TermsConditionScreen from "../screens/Auth/Register/TermsCondition";
import ValidateOTPScreen from "../screens/Auth/Register/ValidateOTP";
import ForgotPasswordScreen from "../screens/Auth/ForgotPassword";
import CheckinScreen from "../screens/Home/Checkin";
import DetailsScreen from "../screens/Home/Details";
import BankAccountScreen from "../screens/Shared/BankAccount";
import UploadDocsScreen from "../screens/Auth/Register/UploadDocs";

// Home
import HomeScreen from "../screens/Home/Home";
import InboxScreen from "../screens/Home/Inbox";
import JobHistoryScreen from "../screens/Home/JobHistory";
import JobDescriptionScreen from "../screens/Home/JobDescription";
import OnboardScreen from "../screens/Home/Onboarding";
import TransactionScreen from "../screens/Home/Transaction";

// Web Profile screens (platform-specific)
import { ProfileDoctorScreen, ProfileFacilitiesScreen } from "../screens/Home";

// Settings
import SettingsScreen from "../screens/Home/Settings/Settings";
import PermissionsScreen from "../screens/Home/Settings/Permissions";
import ChangePasswordScreen from "../screens/Home/Settings/ChangePassword";
import HelpSupportScreen from "../screens/Home/Settings/HelpSupport";
import FeedbackScreen from "../screens/Home/Settings/Feedback";

// Profile
import ProfileScreen from "../screens/Home/Settings/Profile";
import EditProfileScreen from "../screens/Home/Settings/EditProfile";
import EditEducationScreen from "../screens/Home/Settings/EditEducation";
import EditFaciltyScreen from "../screens/Home/Settings/EditFacility";
import EditProfessionScreen from "../screens/Home/Settings/EditProfession";
import EditClinicScreen from "../screens/Home/Settings/EditClinic";

// Jobs - platform-specific screens resolved via index.js
import {
  JobsScreen,
  PostJobScreen,
  SearchFilterScreen,
  SearchLocationScreen,
  MakePaymentScreen,
  SelectBankScreen,
  MyJobsScreen,
  CandidatesScreen,
} from "../screens/Jobs";

// Others
import WebViewScreen from "../screens/Shared/WebView";
import RatingScreen from "../screens/Shared/Rating";
import RegisterUserScreen from "../screens/Auth/Register/RegisterUser";
import RegisterLocationScreen from "../screens/Auth/Register/RegisterLocation";
import StepFacility from "../screens/Auth/Register/StepFacility";
import StepProfession from "../screens/Auth/Register/StepProfession";
import StepType from "../screens/Auth/Register/StepType";
import CancelJobScreen from "../screens/Jobs/CancelJob";

// Doctor Auth (Web)
import DoctorAuthScreen from "../screens/Auth/DoctorAuth/DoctorAuth.web";

// Facility Auth (Web)
import FacilityAuthScreen from "../screens/Auth/FacilityAuth/FacilityAuth.web";

// Public Pages (Web) - accessible without authentication
import LandingScreen from "../screens/Landing";
import PublicJobsScreen from "../screens/Jobs/PublicJobs.web";
import ReachUsScreen from "../screens/Landing/ReachUs.web";
import EmployerScreen from "../screens/Landing/EmployerScreen.web";
import GetAppScreen from "../screens/Landing/GetApp.web";

const Stack = createNativeStackNavigator();
const Tab = createBottomTabNavigator();

const sharedOptions = {
  headerShown: false,
};

function Navigation() {
  const { loggedIn } = useSelector((state) => state.user);

  let MainStack;

  if (loggedIn) {
    // Authenticated users: HomeScreen first
    MainStack = (
      <>
        <Stack.Screen
          name="HomeScreen"
          component={HomeTabs}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="AuthScreen"
          component={AuthStack}
          options={{ gestureEnabled: false }}
        />
      </>
    );
  } else if (Platform.OS === 'web') {
    // Web unauthenticated users: Public Jobs page first
    MainStack = (
      <>
        <Stack.Screen
          name="PublicJobs"
          component={PublicJobsScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="PublicJobDetail"
          component={PublicJobsScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="LandingScreen"
          component={LandingScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="Employer"
          component={EmployerScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="AuthScreen"
          component={AuthStack}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="HomeScreen"
          component={HomeTabs}
          options={{ gestureEnabled: false }}
        />
      </>
    );
  } else {
    // Native unauthenticated users: AuthScreen first
    MainStack = (
      <>
        <Stack.Screen
          name="AuthScreen"
          component={AuthStack}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="HomeScreen"
          component={HomeTabs}
          options={{ gestureEnabled: false }}
        />
      </>
    );
  }

  return (
    <Stack.Navigator
      screenOptions={{
        ...sharedOptions,
      }}
    >
      {MainStack}
      {SettingsStack()}
      {ProfileStack()}
      {JobFlowStack()}
      {SharedStack()}
    </Stack.Navigator>
  );
}

function AuthStack() {
  return (
    <Stack.Navigator
      screenOptions={{
        ...sharedOptions,
      }}
    >
      <Stack.Screen name="LoginScreen" component={LoginScreen} />
      <Stack.Screen
        name="RegisterUserScreen"
        component={RegisterUserScreen}
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen
        name="RegisterLocationScreen"
        component={RegisterLocationScreen}
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen
        name="OnboardScreen"
        component={OnboardScreen}
        options={{ gestureEnabled: false }}
      />


      <Stack.Screen name="UploadDocsScreen" component={UploadDocsScreen} />
      <Stack.Screen name="CategoryScreen" component={CategoryScreen} />

      <Stack.Screen
        name="ValidateOTPScreen"
        component={ValidateOTPScreen}
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen
        name="ForgotPasswordScreen"
        component={ForgotPasswordScreen}
      />
      <Stack.Screen
        name="ClinicLocatorScreen"
        component={ClinicLocatorScreen}
      />
      <Stack.Screen name="ExpertiseScreen" component={ExpertiseScreen} />
      <Stack.Screen name="StepProfession" component={StepProfession} />
      <Stack.Screen name="StepCountry" component={StepCountry} />
      <Stack.Screen name="StepFacility" component={StepFacility} />
      <Stack.Screen name="StepType" component={StepType} />
    </Stack.Navigator>
  );
}

function HomeTabs() {
  const { isDoctor, paymentSetup, notifCount } = useSelector(
    (state) => state.user
  );
  const insets = useSafeAreaInsets();
  if (Platform.OS === "web") {
    return (
      <Stack.Navigator
        screenOptions={{
          ...sharedOptions,
        }}
      >
        <Stack.Screen name="Home" component={HomeScreen} />
      </Stack.Navigator>)
  }

  return (
    
    <Tab.Navigator
        initialRouteName="Home"
        screenOptions={{
          ...sharedOptions,
          tabBarActiveTintColor: "#7176C0",
          tabBarInactiveTintColor: "rgba(0,0,0,0.3)",
          tabBarStyle: {
            height: Platform.OS === "ios"
              ? (aboveX() ? 100 : 65)
              : 65 + insets.bottom, // Increased Android tab height + safe area bottom
            paddingBottom: Platform.OS === "ios"
              ? (aboveX() ? 40 : 10)
              : Math.max(insets.bottom + 5, 15), // Added extra padding for Android
          },
        }}
      >
        <Tab.Screen
          name="Home"
          component={HomeStack}
          options={tabOptions("home-outline")}
        />
        {Platform.OS === "web" ? null : (
          <>
            <Tab.Screen
              name="Jobs"
              component={JobsStack}
              options={tabOptions("briefcase-outline")}
            />
            <Tab.Screen
              name={isDoctor ? " " : " "}
              component={() => <></>}
              listeners={({ navigation }) => ({
                tabPress: (e) => {
                  e.preventDefault();
                  navigation.navigate(
                    isDoctor ? "CheckinScreen" : "PostJobScreen"
                  );
                },
              })}
              options={tabOptions("checkmark-circle-outline", true)}
            />
          </>
        )}

        <Tab.Screen
          name="Activity"
          component={InboxScreen}
          options={tabOptions("mail-outline", false, true, notifCount)}
        />
        <Tab.Screen
          name="Account"
          component={SettingsScreen}
          options={tabOptions("person-circle-outline")}
        />
      </Tab.Navigator>
  );
}

function HomeStack() {
  return (
    <Stack.Navigator
      screenOptions={{
        ...sharedOptions,
      }}
    >
      <Stack.Screen name="HomeScreen" component={HomeScreen} />
    </Stack.Navigator>
  );
}

function JobsStack() {
  const { isDoctor } = useSelector((state) => state.user);

  return (
    <Stack.Navigator
      screenOptions={{
        ...sharedOptions,
      }}
    >
      <Stack.Screen name="JobsScreen" component={JobsScreen} />
    </Stack.Navigator>
  );
}

function SettingsStack() {
  return (
    <>
      <Stack.Screen name="HelpSupportScreen" component={HelpSupportScreen} />
      <Stack.Screen name="FeedbackScreen" component={FeedbackScreen} />
      <Stack.Screen name="WebViewScreen" component={WebViewScreen} />
      <Stack.Screen name="PermissionsScreen" component={PermissionsScreen} />
      <Stack.Screen
        name="ChangePasswordScreen"
        component={ChangePasswordScreen}
      />
    </>
  );
}

function ProfileStack() {
  return (
    <>
      <Stack.Screen name="ProfileScreen" component={ProfileScreen} />
      <Stack.Screen name="EditProfileScreen" component={EditProfileScreen} />
      <Stack.Screen
        name="EditEducationScreen"
        component={EditEducationScreen}
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen name="EditFaciltyScreen" component={EditFaciltyScreen} />
      <Stack.Screen name="EditProfessionScreen" component={EditProfessionScreen} />
      <Stack.Screen name="EditClinicScreen" component={EditClinicScreen} />
      <Stack.Screen name="SelectBankScreen" component={SelectBankScreen} />
    </>
  );
}

function JobFlowStack() {
  return (
    <>
      <Stack.Screen name="DetailsScreen" component={DetailsScreen} />
      <Stack.Screen
        name="JobDescriptionScreen"
        component={JobDescriptionScreen}
      />
      <Stack.Screen
        name="SearchLocationScreen"
        component={SearchLocationScreen}
      />
      <Stack.Screen name="SearchFilterScreen" component={SearchFilterScreen} />
      <Stack.Screen
        options={{ presentation: "modal" }}
        name="PaymentSetupScreen"
        component={PaymentSetupScreen}
      />
      <Stack.Screen
        // options={{ presentation: "modal" }}
        name="PostProfessionScreen"
        component={StepProfession}
      />
      <Stack.Screen name="PostJobScreen" component={StepType} />
      <Stack.Screen name="PostJobExpertiseScreen" component={ExpertiseScreen} />
      <Stack.Screen name="PostJobCategoryScreen" component={CategoryScreen} />

      <Stack.Screen name="PostJobMainScreen" component={PostJobScreen} />
      <Stack.Screen
        options={{ presentation: "modal" }}
        name="CheckinScreen"
        component={CheckinScreen}
      />
      <Stack.Screen
        options={{ presentation: "modal" }}
        name="DoctorProfileScreen"
        component={ProfileScreen}
      />
      <Stack.Screen
        name="MakePaymentScreen"
        component={MakePaymentScreen}
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen name="TransactionScreen" component={TransactionScreen} />
    </>
  );
}

function SharedStack() {
  return (
    <>
      <Stack.Screen
        name="BankAccountScreen"
        component={BankAccountScreen}
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen
        name="CompleteRegistrationScreen"
        component={CompleteRegistrationScreen}
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen
        name="RatingScreen"
        component={RatingScreen}
        options={{ presentation: "modal" }}
      />
      <Stack.Screen
        name="RegisterScreen"
        component={RegisterScreen}
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen name="CancelJobScreen" component={CancelJobScreen} />
      <Stack.Screen
        // options={{ presentation: "modal" }}
        name="TermsConditionScreen"
        component={TermsConditionScreen}
      />
      {/* Web-exclusive screens */}
      {Platform.OS === 'web' && (
        <>
          {/* Public pages (no auth required) */}
          <Stack.Screen name="ReachUs" component={ReachUsScreen} />
          <Stack.Screen name="GetApp" component={GetAppScreen} />

          {/* Authenticated pages */}
          <Stack.Screen name="Jobs" component={JobsScreen} />
          <Stack.Screen name="JobDescription" component={JobDescriptionScreen} />
          <Stack.Screen name="Inbox" component={InboxScreen} />
          <Stack.Screen name="JobHistory" component={JobHistoryScreen} />
          <Stack.Screen
            name="PostJob"
            component={PostJobScreen}
            options={{ presentation: "modal" }}
          />
          <Stack.Screen name="MyJobs" component={MyJobsScreen} />
          <Stack.Screen name="Candidates" component={CandidatesScreen} />
          <Stack.Screen name="ProfileDoctor" component={ProfileDoctorScreen} />
          <Stack.Screen name="ProfileFacility" component={ProfileFacilitiesScreen} />
          <Stack.Screen name="DoctorAuth" component={DoctorAuthScreen} />
          <Stack.Screen name="FacilityAuth" component={FacilityAuthScreen} />
        </>
      )}
    </>
  );
}

const tabOptions = (
  iconName,
  isCheckin = false,
  isInbox = false,
  notifCount
) => {
  if (isCheckin) {
    return {
      tabBarIcon: ({ color, size }) => (
        <CheckinButton iconName={iconName} color={color} size={size} />
      ),
    };
  } else {
    return {
      tabBarIcon: ({ color, size }) => (
        <>
          {isInbox && notifCount > 0 ? (
            <View
              style={{
                top: 8,
                right: 16,
                position: "absolute",
                backgroundColor: "rgba(255, 84, 110,1)",
                zIndex: 10,
                borderRadius: 20,
                width: 20,
                height: 20,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text style={{ color: "white", fontSize: 11 }}>{notifCount}</Text>
            </View>
          ) : null}
          <View style={{ marginBottom: -10 }}>
            <Ionicons name={iconName} color={color} size={size} />
          </View>
        </>
      ),
    };
  }
};

export default Navigation;
